import React, { useMemo } from "react";
import { Group } from "react-konva";
import { OrgChartOrientation } from "shared/datamodel/schemas/org-chart";
import { enablePatches } from "immer";
import * as utils from "../orgchart-utils";
import * as treeUtils from "../shared/tree-layout-utils";
import { hierarchy, tree } from "d3-hierarchy";
import { Degrees } from "frontend/utils/transform";
import SimpleConnector from "../../connector/simple-connector";
import { TEXT_NODE_HEIGHT } from "shared/datamodel/mindmap-org-chart";

enablePatches();

export interface NodeDrawProps {
  lineWidth: number;
  dash: number;
  color: string;
}

interface EdgeDrawProps {
  strokeWidth: number;
  stroke: string;
  dash: number;
}

const SeparationLayers = 100;
const SeparationSiblings = 1.5;

export function MindmapOrgChartComponent({
  x,
  y,
  rootNodeId,
  layout,
  data,
  orientation,
  nodeHeight,
  nodeWidth,
  getEdgeDrawProps,
  selectedFieldsLength,
  renderNode,
  renderCollapseButton,
  renderAddNodeButtons,
  shouldEnableDepartmentAsRoot,
}: {
  x: number;
  y: number;
  rootNodeId: string;
  layout: Record<string, utils.NodeLayout>;
  data: Record<string, any>;
  orientation: OrgChartOrientation;
  nodeHeight: number;
  nodeWidth: number;
  getEdgeDrawProps: (childNodeId: string) => EdgeDrawProps;
  selectedFieldsLength?: number;
  renderNode: (
    nodeId: string,
    x: number,
    y: number,
    height: number,
    data: any,
    isRoot: boolean,
    rootType: utils.SourceNodeType,
    children?: React.ReactNode
  ) => React.ReactNode;
  renderCollapseButton: any;
  renderAddNodeButtons: any;
  shouldEnableDepartmentAsRoot: boolean;
}) {
  const { rootLayout, rootId, rootType } = utils.getRootNodeData(rootNodeId, layout, shouldEnableDepartmentAsRoot);

  const descendants = useMemo(
    () => utils.calcDescCount(utils.makeTreeLayoutFromOrgChartData(layout), rootId),
    [layout]
  );
  const layoutUtil = treeUtils.TreeLayoutHelper.from(orientation, nodeWidth, nodeHeight, SeparationLayers);
  const convert = treeUtils.LayoutProps[orientation].convert;

  const the_tree = useMemo(() => {
    const seenNodes = new Set();
    seenNodes.add(rootId);
    const root = hierarchy(rootLayout, (d) => {
      if (!d.childrenIds || d.collapsed) return null;
      const childIds: Array<any> = d.childrenIds
        .map((id: any) => layout[id])
        .filter((child: any) => !seenNodes.has(child.id));
      // Add seen ndoe ids to prevent recursions
      childIds.forEach((child) => seenNodes.add(child.id));
      return childIds;
    });
    const nodeSize = layoutUtil.getNodeSizeForD3tree();
    const treeLayoutMaker = tree<any>()
      .nodeSize(nodeSize)
      .separation(() => SeparationSiblings);
    return treeLayoutMaker(root);
  }, [layout, orientation, nodeHeight]);

  const edges: React.ReactNode[] = [];
  const nodes: React.ReactNode[] = [];
  const collapseButtons: React.ReactNode[] = [];

  // Rendering nodes (if root isn't collapsed)
  //
  // TODO: some of this stuff was written to emulate OrgChartCanvasElement component, but then that
  // changed, so this code is a relic of that time. It should be cleaned up.
  if (!the_tree.data.collapsed) {
    the_tree.eachAfter((node) => {
      if (!node.parent) return null; // root is drawn differently
      const myId = node.data.id; //utils.makeNodeId(rootid, node.data.id);
      let [x, y] = convert(node.x, node.y);

      const total = descendants[node.data.id];
      const direct = layout[node.data.id].childrenIds.length;
      if (direct > 0 && renderCollapseButton) {
        const collapsed = !node.children;
        collapseButtons.push(renderCollapseButton(node.data.id, collapsed, x, y, direct, total));
      }

      const [parentX, parentY] = convert(node.parent.x, node.parent.y);
      const selfOfs = layoutUtil.getNodeSocket("toRoot", 1);
      const parentOfs = layoutUtil.getNodeSocket("toLeaves", 1);
      edges.push(
        <SimpleConnector
          key={node.data.id}
          id={node.data.id}
          p1={{
            x: parentX + parentOfs.x,
            y: parentY + parentOfs.y,
            rotation: parentOfs.rotation as Degrees,
          }}
          p2={{ x: x + selfOfs.x, y: y + selfOfs.y, rotation: selfOfs.rotation as Degrees }}
          lineType={"curve"}
          element={getEdgeDrawProps(node.data.id)}
          curveStrength={50}
        />
      );
      nodes.push(renderNode(myId, x, y, nodeHeight, data[node.data.id], false, utils.SourceNodeType.Person));
    });
  }

  if (layout[rootId].childrenIds.length > 0 && renderCollapseButton) {
    const collapsed = the_tree.data.collapsed;
    const total = descendants[rootId];
    const direct = layout[rootId].childrenIds.length;
    const center = convert(the_tree.x, the_tree.y);
    collapseButtons.push(renderCollapseButton(rootId, collapsed, center[0], center[1], direct, total));
  }

  const rootNodeHeight = utils.isDepartmentAsSource(rootType, shouldEnableDepartmentAsRoot)
    ? TEXT_NODE_HEIGHT
    : nodeHeight;

  const rootNodeVerticalCorrection = utils.getRootNodeVerticalCorrection(
    rootType,
    orientation,
    rootNodeHeight,
    selectedFieldsLength,
    shouldEnableDepartmentAsRoot
  );

  const rootNode = renderNode(
    rootId,
    the_tree.x,
    the_tree.y + rootNodeVerticalCorrection,
    rootNodeHeight,
    data[the_tree.data.id],
    true,
    rootType
  );

  return (
    <>
      {rootNode}
      <Group
        x={x}
        y={y}
        id={`${
          typeof rootNode === "object" && rootNode !== null && "props" in rootNode ? rootNode.props.id : rootId
        }-wrapper`}
      >
        {edges}
        {nodes}
        {collapseButtons}
        {renderAddNodeButtons && renderAddNodeButtons()}
      </Group>
    </>
  );
}
